import React, { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Modal,
  Paper,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import debounce from 'lodash.debounce';
import api from '../services/api';
import { USE_MOCK } from '../config/api';
import { encrypt } from '../utils/crypto';

interface Motorista {
  id: number;
  nome: string;
  cnh: string;
  placa_vei: string;
  cod_transp: string;
}

interface CheckinFormProps {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

// --- Funções auxiliares ---

// Formata CPF corretamente para 000.000.000-00
const formatCPF = (value: string) => {
  // remove tudo que não for número e limita a 11 dígitos
  const digits = value.replace(/\D/g, '').slice(0, 11);

  // aplica as máscaras progressivamente
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};


// Valida se o CPF é válido de acordo com os dígitos verificadores
const isValidCPF = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, ''); // só números

  if (cleaned.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleaned)) return false; // rejeita CPFs com todos os dígitos iguais

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cleaned.charAt(i)) * (10 - i);
  let firstCheck = (sum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== parseInt(cleaned.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cleaned.charAt(i)) * (11 - i);
  let secondCheck = (sum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;
  if (secondCheck !== parseInt(cleaned.charAt(10))) return false;

  return true;
};


const onlyDigits = (value: string) => value.replace(/\D/g, '');

const CheckinForm: React.FC<CheckinFormProps> = ({ onSuccess, onError }) => {
  const [cpf, setCpf] = useState('');
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [codTransp, setCodTransp] = useState('');
  const [cpfError, setCpfError] = useState('');

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [foundDriver, setFoundDriver] = useState < Motorista | null > (null);

  const [openModal, setOpenModal] = useState(false);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);


    // Validação em tempo real (opcional)
    if (formatted.length === 14 && !isValidCPF(formatted)) {
      setCpfError('CPF inválido');
    } else {
      setCpfError('');
    }
  };

  const fetchMotoristaByCPF = async (cpfDigits: string) => {
    if (cpfDigits.length < 11) return;

    setLoadingSearch(true);
    try {
      const { data } = await api.get(`/motoristas?cpf=${encrypt(cpfDigits ?? '')}`);


      // Trata retorno como objeto único ou array
      const motorista = Array.isArray(data) ? data[0] : data;

      if (motorista && motorista.nome) {
        // Encontrou motorista cadastrado
        setFoundDriver(motorista);
        setName(motorista.nome);
        setPlate(motorista.placa_vei);
        setCodTransp(motorista.cod_transp);
      } else {
        // Nenhum motorista encontrado — permitir cadastro manual
        setFoundDriver(null);
        setName('');
        setPlate('');
      }

      setOpenModal(true);
    } catch (err: any) {
      const status = err.response?.status;
      const message = err.response?.data?.error || '';

      // Se for "não encontrado" (400 ou 404), abre o modal de cadastro manual
      if (
        status === 404 ||
        (status === 400 && message.toLowerCase().includes('não encontrado'))
      ) {
        setFoundDriver(null);
        setName('');
        setPlate('');
        setOpenModal(true);
      } else {
        console.error('Erro ao buscar motorista:', err);
        onError('Erro ao buscar motorista. Tente novamente mais tarde.');
      }
    } finally {
      setLoadingSearch(false);
    }
  };




  /*   const fetchMotoristaByCPF = async (cpfDigits: string) => {
      if (cpfDigits.length < 11) return;
      setLoadingSearch(true);
      try {
        const { data } = await api.get(`/motoristas/search`, {
          params: { term: cpfDigits },
        });
  
        if (data && data.length > 0) {
          setFoundDriver(data[0]);
          setName(data[0].nome);
          setPlate(data[0].placa_vei);
          setCodTransp(data[0].cod_transp);
        } else {
          setFoundDriver(null);
          setName('');
          setPlate('');
        }
        setOpenModal(true);
      } catch (err) {
        onError('Erro ao buscar motorista.');
      } finally {
        setLoadingSearch(false);
      }
    }; */

  const debouncedSearch = useMemo(
    () => debounce(fetchMotoristaByCPF, 400),
    []
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onError('');
    onSuccess('');
    debouncedSearch(onlyDigits(cpf));
  };

  const handleSubmit = async () => {
    onError('');
    onSuccess('');
    setSubmitting(true);

    try {
      const stored = localStorage.getItem('selectedDeposito');
      if (!stored) throw new Error('Depósito não encontrado.');

      const payload = {
        cnh: '', // removido do fluxo
        cod_transp: codTransp || '',
        driver_name: name.trim(),
        license_plate: plate.trim().toUpperCase(),
        cpf: onlyDigits(cpf),
      };

      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 700));
        onSuccess('Check-in registrado (modo mock).');
      } else {
        const { data } = await api.post(`/checkin`, payload);
        onSuccess(data.message || 'Check-in registrado com sucesso.');
      }

      setCpf('');
      setName('');
      setPlate('');
      setFoundDriver(null);
      setOpenModal(false);
    } catch (err: any) {
      onError(err?.message || 'Erro ao registrar check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFoundDriver(null);
  };

  return (
    <Box component="form" onSubmit={handleSearch} noValidate>
      <Stack spacing={3}>
        <TextField
          label="CPF"
          value={cpf}
          onChange={handleCpfChange}
          placeholder="000.000.000-00"
          inputProps={{ inputMode: 'numeric' }}
          helperText={cpfError || 'Digite seu CPF para buscar o cadastro'}
          error={!!cpfError}
          fullWidth
          required
          variant="outlined"
        />


        <Button
          variant="contained"
          type="submit"
          disabled={loadingSearch || !isValidCPF(cpf)}
          size="large"
        >
          {loadingSearch ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Buscar'
          )}
        </Button>
      </Stack>

      {/* Modal de confirmação */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.4)',
          }}
          onClick={handleCloseModal}
        >
          <Paper
            onClick={(e) => e.stopPropagation()} // não fecha ao clicar dentro
            sx={{
              position: 'relative',
              width: '90%',
              maxWidth: 420,
              p: 4,
              borderRadius: 3,
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <Close />
            </IconButton>

            {foundDriver ? (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Confirmação de Check-in
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Motorista:</strong> {foundDriver.nome}
                  <br />
                  {/*
                  <strong>Placa:</strong> {foundDriver.placa_vei}


                  */}
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Confirmar Check-in'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Novo Motorista
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Não encontramos seu CPF. Preencha seus dados para registrar o
                  check-in:
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Nome Completo"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Placa do Veículo"
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    helperText="Formato: 7 caracteres (ex: ABC1234)"
                    fullWidth
                    required
                  />

                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Registrar Check-in'
                    )}
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Box>
      </Modal>
    </Box>
  );
};

export default CheckinForm;

